'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Search, Calendar, User, BookOpen, ArrowRight, X } from 'lucide-react';
import { Blog } from '@/lib/api';

interface BlogSearchProps {
  initialBlogs: Blog[];
  site: 'consumer' | 'business';
  variant: 'consumer' | 'zakelijk';
}

export function BlogSearch({ initialBlogs, site, variant }: BlogSearchProps) {
  const [search, setSearch] = useState('');
  const [blogs, setBlogs] = useState<Blog[]>(initialBlogs);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Debounced search
  useEffect(() => {
    if (!search.trim()) {
      setBlogs(initialBlogs);
      setHasSearched(false);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const params = new URLSearchParams({ page: '0', size: '20', search });
        const hostname = window.location.hostname;
        const apiUrl = hostname.includes('test.')
          ? 'https://test.api.valbewust.nl/api/v1'
          : hostname === 'localhost'
            ? 'http://localhost:8081/api/v1'
            : 'https://api.valbewust.nl/api/v1';
        const response = await fetch(`${apiUrl}/public/blogs/${site}?${params}`);
        if (response.ok) {
          const data = await response.json();
          setBlogs(data.content);
          setHasSearched(true);
        }
      } catch (error) {
        console.error('Search failed:', error);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [search, site, initialBlogs]);

  const clearSearch = useCallback(() => {
    setSearch('');
    setBlogs(initialBlogs);
    setHasSearched(false);
  }, [initialBlogs]);

  return (
    <>
      {/* Search bar - matching gemeente style */}
      <div className="mb-8">
        <div className="relative max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-ink/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Zoek artikelen..."
            className="w-full pl-12 pr-10 py-3 bg-white border border-ink/10 rounded-full text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-cta/20 focus:border-cta transition-all"
          />
          {search && (
            <button
              onClick={clearSearch}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ink transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
        {hasSearched && (
          <p className="mt-3 text-ink/60 text-sm">
            {blogs.length === 0
              ? 'Geen artikelen gevonden'
              : `${blogs.length} artikel${blogs.length === 1 ? '' : 'en'} gevonden`}
          </p>
        )}
      </div>

      {/* Loading state */}
      {isSearching && (
        <div className="text-center py-8">
          <div className={`w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto ${
            variant === 'zakelijk' ? 'border-ink' : 'border-cta'
          }`} />
        </div>
      )}

      {/* Blog grid */}
      {!isSearching && (
        <BlogGrid blogs={blogs} variant={variant} hasSearched={hasSearched} />
      )}
    </>
  );
}

function BlogGrid({ blogs, variant, hasSearched }: { blogs: Blog[]; variant: 'consumer' | 'zakelijk'; hasSearched: boolean }) {
  if (blogs.length === 0) {
    // Don't show empty state when search returned no results (already shown above search bar)
    if (hasSearched) {
      return null;
    }
    return (
      <div className="text-center py-16">
        <div className={`w-20 h-20 ${variant === 'zakelijk' ? 'bg-ink/10' : 'bg-cta/10'} rounded-full flex items-center justify-center mx-auto mb-6`}>
          <BookOpen className={`w-10 h-10 ${variant === 'zakelijk' ? 'text-ink/40' : 'text-cta'}`} />
        </div>
        <h2 className="text-2xl font-bold text-ink mb-3">Nog geen artikelen</h2>
        <p className="text-lg text-ink/60">Er zijn nog geen blogartikelen gepubliceerd.</p>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {blogs.map((blog) => (
        <article
          key={blog.id}
          className={`bg-white overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 ${
            variant === 'zakelijk' ? 'rounded-2xl card-hover' : 'rounded-3xl'
          }`}
        >
          {blog.imageUrl ? (
            <div className={`relative h-48 overflow-hidden ${variant === 'zakelijk' ? 'bg-ink/5' : 'bg-cta/10'}`}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={blog.imageUrl}
                alt={blog.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          ) : (
            <div className={`h-48 flex items-center justify-center ${
              variant === 'zakelijk'
                ? 'bg-gradient-to-br from-ink/5 to-ink/10'
                : 'bg-gradient-to-br from-cta/10 to-cta/20'
            }`}>
              <BookOpen className={`w-16 h-16 ${variant === 'zakelijk' ? 'text-ink/20' : 'text-cta/40'}`} />
            </div>
          )}
          <div className="p-6">
            <div className="flex items-center gap-4 text-sm text-ink/50 mb-3">
              {blog.publishedAt && (
                <div className="flex items-center gap-1">
                  <Calendar size={14} />
                  {new Date(blog.publishedAt).toLocaleDateString('nl-NL', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </div>
              )}
              {blog.authorName && (
                <div className="flex items-center gap-1">
                  <User size={14} />
                  {blog.authorName}
                </div>
              )}
            </div>
            <h2 className="text-xl font-bold text-ink mb-3 line-clamp-2">
              {blog.title}
            </h2>
            {blog.summary && (
              <p className="text-ink/70 mb-4 line-clamp-3">{blog.summary}</p>
            )}
            <Link
              href={`/blog/${blog.slug}`}
              className={`inline-flex items-center font-semibold transition-colors ${
                variant === 'zakelijk'
                  ? 'text-ink hover:text-ink/70'
                  : 'text-cta hover:text-cta-dark'
              }`}
            >
              Lees meer
              <ArrowRight size={16} className="ml-1" />
            </Link>
          </div>
        </article>
      ))}
    </div>
  );
}
